import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { ValuationShell } from '@/components/valuation/ValuationShell'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ValuationGate } from '@/components/valuation/ValuationGate'
import { Login } from '@/pages/Login'
import { ForgotPassword } from '@/pages/ForgotPassword'
import { ResetPassword } from '@/pages/ResetPassword'
import { RequestAccess } from '@/pages/RequestAccess'
import { GettingStarted } from '@/pages/GettingStarted'
import { AccountPage } from '@/pages/AccountPage'
import ValuationDashboard from '@/pages/valuation/ValuationDashboard'
import ValuationHistory from '@/pages/valuation/ValuationHistory'
import ValuationProfileSelect from '@/pages/valuation/ValuationProfileSelect'
import ValuationCompany from '@/pages/valuation/ValuationCompany'
import ValuationFinancials from '@/pages/valuation/ValuationFinancials'
import ValuationNormalize from '@/pages/valuation/ValuationNormalize'
import ValuationIntake from '@/pages/valuation/ValuationIntake'
import ValuationTuning from '@/pages/valuation/ValuationTuning'
import ValuationOutput from '@/pages/valuation/ValuationOutput'
import ValuationSignOff from '@/pages/valuation/ValuationSignOff'
import ValuationOverview from '@/pages/valuation/ValuationOverview'
import { PrivacyPolicyPage } from '@/pages/legal/PrivacyPolicyPage'
import { DataRetentionPage } from '@/pages/legal/DataRetentionPage'
import { TermsOfServicePage } from '@/pages/legal/TermsOfServicePage'
import { useAuth } from '@/contexts/AuthContext'
import { landingPathForUser } from '@/lib/roleLanding'

/**
 * VAL-DEPLOY-UNIT-1 — trimmed route table for the standalone Valuation
 * build. Deliberately NOT an import of `@/router` (the QoE monolith) or
 * `@/router/attest` — mirrors `ATTEST-DEPLOY-UNIT-1`'s reasoning exactly:
 * `@/router` eagerly imports every product's pages regardless of this
 * file's own route list, and the entire point of a second build entry is a
 * bundle that only contains what Valuation actually uses.
 *
 * What's reused as-is (confirmed product-neutral by R1's import inspection):
 * AppShell, ProtectedRoute, Login/ForgotPassword/ResetPassword/RequestAccess,
 * GettingStarted (forced password-change target — reachable via
 * ProtectedRoute, so it must exist here or that redirect dead-ends),
 * AccountPage (USER-PROFILE-1: ungated, every authenticated user has one),
 * the three legal pages, and landingPathForUser/useAuth.
 *
 * `/valuation` and its four sub-routes are wrapped in `ValuationGate` only
 * — not `ProtectedRoute allowedRoles` — matching `router/index.tsx`'s own
 * comment at that block: ValuationGate does the deny with a stated reason
 * (firm entitlement, then role), which is the correct behaviour for a
 * `valuation_owner`/`valuation_signer` account that has no QoE dashboard to be
 * bounced to.
 *
 * What's deliberately NOT here: /hub, /dashboard, /seller, /buyer,
 * /firm-profile, all of Normalization/DealDetail/AdminDashboard/Attest —
 * none of those are reachable from a Valuation-only deploy unit's own App
 * Service, matching the OTCID/QoE/Attest precedent of fully separate App
 * Services per product. Both valuation roles' landingPathForUser already
 * resolves to /valuation (roleLanding.ts) without a /hub hop, so nothing is
 * lost by leaving it out. `/firm-profile` is left out deliberately too:
 * neither `valuation_signer` nor `valuation_owner` is granted
 * `platform:firm_profile` (entitlements.py), unlike Attest where the
 * signing identity the letters read lives there.
 *
 * Known, pre-existing, not-fixed-here gap (recorded in
 * docs/DEV_VERIFICATION_LOG.md): `ValuationGate.tsx`'s role-denied panel
 * links to `/dashboard`, which does not exist in this trimmed route table.
 * That is a product-behaviour string this Ring-0 deploy-topology brief does
 * not change.
 */

// Every path this trimmed route table actually serves. PLAT-FIRST-LOGIN-2
// R2: landingPathForUser(user) can resolve to '/hub' or '/dashboard' (a
// multi-product super_admin, or the role-map fallback) — neither exists
// here, so falling through to '*' -> DefaultRedirect -> the same unmapped
// path was an infinite loop that rendered nothing.
const VALUATION_ROUTES = new Set([
  '/getting-started', '/account',
  '/valuation',
])

function DefaultRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()
  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />
  const target = landingPathForUser(user)
  const isServed = VALUATION_ROUTES.has(target) || target.startsWith('/valuation/')
  return <Navigate to={isServed ? target : '/valuation'} replace />
}

export const valuationRouter = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/request-access', element: <RequestAccess /> },
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  { path: '/data-retention', element: <DataRetentionPage /> },
  { path: '/terms', element: <TermsOfServicePage /> },
  { path: '/', element: <DefaultRedirect /> },
  {
    element: (
      <ProtectedRoute>
        <ValuationShell />
      </ProtectedRoute>
    ),
    children: [
      { path: '/getting-started', element: <GettingStarted /> },
      { path: '/account', element: <AccountPage /> },
      // Mirrors router/index.tsx's ValuationGate-only gating exactly (no
      // ProtectedRoute allowedRoles layer here — see file comment above).
      { path: '/valuation', element: <ValuationGate><ValuationDashboard /></ValuationGate> },
      { path: '/valuation/history', element: <ValuationGate><ValuationHistory /></ValuationGate> },
      { path: '/valuation/new', element: <ValuationGate><ValuationProfileSelect /></ValuationGate> },
      { path: '/valuation/:valuationId/overview', element: <ValuationGate><ValuationOverview /></ValuationGate> },
      { path: '/valuation/:valuationId/company', element: <ValuationGate><ValuationCompany /></ValuationGate> },
      { path: '/valuation/:valuationId/financials', element: <ValuationGate><ValuationFinancials /></ValuationGate> },
      { path: '/valuation/:valuationId/adjust', element: <ValuationGate><ValuationNormalize /></ValuationGate> },
      { path: '/valuation/:valuationId/intake', element: <ValuationGate><ValuationIntake /></ValuationGate> },
      { path: '/valuation/:valuationId/tuning', element: <ValuationGate><ValuationTuning /></ValuationGate> },
      { path: '/valuation/:valuationId/output', element: <ValuationGate><ValuationOutput /></ValuationGate> },
      { path: '/valuation/:valuationId/signoff', element: <ValuationGate><ValuationSignOff /></ValuationGate> },
    ],
  },
  { path: '*', element: <DefaultRedirect /> },
])
